import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  FormStateContext,
  FormEntityContext,
} from 'app/frontend/base/config/context/forms'
import { useUpdateEntity } from 'app/frontend/base/config/mutations'
import { PropsWithChildren, useContext, useEffect, useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z, ZodTypeAny } from 'zod'
import { deleteRequest } from '../api'
import { displayErrorToast } from '../helpers'

interface Identifiable {
  id?: number
}

export interface SubmitButtonsProps {
  isPending: boolean
  isDeleting: boolean
  setValue: (name: string, value: unknown) => void
}

interface Props<T> {
  formSchema: ZodTypeAny
  entity: T
  entityType: string
  parentEntityType?: string
  parentId?: number
  onSave?: (form: UseFormReturn, data: RecursivePartial<T>) => void
  extraActions?: React.ReactNode[]
  allowDelete?: boolean
  i18nPrefix?: string
  stringifyValues?: (keyof T)[]
  transformValues?: (values: z.infer<ZodTypeAny>) => z.infer<ZodTypeAny>
  className?: string
  submitButtons?: (props: SubmitButtonsProps) => React.ReactNode
  showSuccessToast?: boolean
  customSubmit?: (
    form: UseFormReturn,
    values: z.infer<ZodTypeAny>,
  ) => Promise<void> | void
}

const FormWrapper = <T extends Identifiable>({
  formSchema,
  entity,
  entityType,
  parentEntityType,
  parentId,
  onSave,
  extraActions,
  allowDelete,
  transformValues,
  children,
  className,
  stringifyValues,
  i18nPrefix = 'forms.fields',
  submitButtons,
  showSuccessToast = true,
  customSubmit,
}: PropsWithChildren<Props<T>>) => {
  const { t } = useTranslation()
  const { setHasUnsavedChanges } = useContext(FormStateContext)
  const [deleting, setDeleting] = useState(false)
  const mutation = useUpdateEntity<T>({
    entityType,
    id: entity.id,
    queryKey: entityType === 'team' ? 'path' : 'id',
    queryKeyValue:
      entityType === 'team'
        ? (entity as unknown as wowaudit.Team).path
        : entity.id,
    parentEntityType,
    parentId,
    stringifyValues,
    showSuccessToast,
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: entity,
  })
  const { isDirty, dirtyFields } = form.formState

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const transformedValues = transformValues ? transformValues(values) : values

    if (customSubmit) {
      await customSubmit(form, transformedValues)
      return
    }

    mutation.mutate(transformedValues, {
      onSuccess: (data) => {
        if (onSave) {
          onSave(form, data)
        } else {
          form.reset(data)
        }
      },
    })
  }
  const isSubmitting = customSubmit
    ? form.formState.isSubmitting
    : mutation.isPending

  useEffect(() => {
    const isReallyDirty = isDirty && Object.keys(dirtyFields).length > 0
    if (setHasUnsavedChanges) setHasUnsavedChanges(isReallyDirty)

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isReallyDirty) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty, dirtyFields.length])

  if (Object.keys(form.formState.errors).length) {
    console.log({ formErrors: form.formState.errors })
  }

  return (
    <FormEntityContext.Provider value={{ entityType, i18nPrefix }}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {children}

          <div
            className={`flex justify-end mt-6 gap-4 items-center ${
              className ?? ''
            }`}
          >
            {Object.keys(form.formState.errors).length ? (
              <span className='text-destructive-foreground font-semibold'>
                {t('shared.errors-found')}
              </span>
            ) : null}

            {extraActions ? extraActions : null}

            {allowDelete && entity.id ? (
              <Button
                variant='destructive'
                size='sm'
                type='button'
                loading={deleting}
                disabled={isSubmitting}
                onClick={() => {
                  if (confirm(t('shared.delete-confirm'))) {
                    const baseUrl = parentEntityType
                      ? `/api/${parentEntityType}s/${parentId}/${entityType}s`
                      : `/api/${entityType}s`

                    deleteRequest(`${baseUrl}/${entity.id}`)
                      .then(() => {
                        setDeleting(false)
                        if (onSave) onSave(form, entity as RecursivePartial<T>)
                      })
                      .catch((err) => {
                        displayErrorToast(err, t)
                        setDeleting(false)
                      })
                  }
                }}
              >
                {t('shared.delete')}
              </Button>
            ) : null}
            {submitButtons ? (
              submitButtons({
                isPending: isSubmitting,
                isDeleting: deleting,
                setValue: form.setValue,
              })
            ) : (
              <Button
                type='submit'
                disabled={deleting}
                loading={isSubmitting}
                size='sm'
              >
                {entity.id ? t('shared.save-changes') : t('shared.save-new')}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </FormEntityContext.Provider>
  )
}

export default FormWrapper
